import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Car,
  ChevronRight,
  Crosshair,
  Home,
  Loader2,
  LogIn,
  LogOut,
  MapPin,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../../../components/ui/select';
import GeofenceCirclePicker, {
  MAX_RADIUS,
  MIN_RADIUS,
} from '../../../components/GeofenceCirclePicker';
import { useApi } from '../../../hooks/useApi';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  buildGeofencePayload,
  createCustomerGeofence,
  extractList,
  extractRecord,
  fetchCustomerGeofences,
  formatRadius,
  normalizeGeofence,
  updateCustomerGeofence,
} from '../../../utils/geofence';
import { GEOFENCE_DARK as D } from '../../../utils/geofenceTheme';

const emptyForm = {
  name: 'Home',
  vehicleId: '',
  deviceId: '',
  latitude: '',
  longitude: '',
  radiusMeters: 500,
  alertOnEnter: false,
  alertOnExit: false,
  enabled: true,
};

const LIGHT = {
  mainBg: '#FAFAFA',
  cardBg: '#FFFFFF',
  inputBg: '#F4F4F5',
  inputBorder: '#E4E4E7',
  divider: '#F4F4F5',
  primaryText: '#18181B',
  whiteText: '#27272A',
  secondaryText: '#71717A',
  placeholder: '#A1A1AA',
  buttonBg: '#18181B',
  buttonText: '#FFFFFF',
  buttonPressed: '#27272A',
  buttonDisabled: '#A1A1AA',
  iconBg: '#F4F4F5',
  switchOff: '#D4D4D8',
  switchThumb: '#FFFFFF',
  switchOn: '#18181B',
  enterIconBg: '#DCFCE7',
  enterIcon: '#16A34A',
  exitIconBg: '#FEE2E2',
  exitIcon: '#DC2626',
  sliderInactive: '#E4E4E7',
  shadow: '0 1px 2px rgba(0,0,0,0.06)',
};

export default function GeofenceForm() {
  const { geofenceId } = useParams();
  const isEdit = Boolean(geofenceId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { get, post, put, patch } = useApi();
  const { isDarkMode } = useTheme();
  const mapSectionRef = useRef(null);
  const C = isDarkMode ? D : LIGHT;

  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [mapFocusKey, setMapFocusKey] = useState(0);

  const preselectVehicleId = searchParams.get('vehicleId') || '';
  const preselectDeviceId = searchParams.get('deviceId') || '';

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => String(v.id) === String(form.vehicleId)) || null,
    [vehicles, form.vehicleId]
  );

  const radiusNum = Number.parseInt(String(form.radiusMeters), 10);
  const safeRadius =
    Number.isFinite(radiusNum) && radiusNum >= MIN_RADIUS
      ? Math.min(MAX_RADIUS, radiusNum)
      : 500;

  const hasCoords =
    Number.isFinite(Number.parseFloat(form.latitude)) &&
    Number.isFinite(Number.parseFloat(form.longitude));

  const coordsLabel = hasCoords
    ? `${Number(form.latitude).toFixed(6)}, ${Number(form.longitude).toFixed(6)}`
    : 'Not set — tap map or use GPS';

  const loadVehicles = useCallback(async () => {
    const response = await get('/customer/my-vehicles?per_page=1000&page=1');
    const list = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];

    return list
      .map((vehicle) => {
        const id = vehicle?.vehicle_id || vehicle?.id || vehicle?._id;
        return {
          id,
          vehicleNumber: vehicle?.vehicle_number || vehicle?.vehicleNumber || 'N/A',
          vehicleName: vehicle?.vehicle_name || vehicle?.vehicleName || 'N/A',
          deviceId: vehicle?.device_id || vehicle?.deviceId || null,
        };
      })
      .filter((v) => v.id);
  }, [get]);

  const loadExisting = useCallback(async () => {
    if (!geofenceId) return null;

    try {
      const detail = await get(`/customer/geofences/${geofenceId}`);
      return normalizeGeofence(extractRecord(detail));
    } catch {
      const { data } = await fetchCustomerGeofences(get);
      const match = extractList(data)
        .map(normalizeGeofence)
        .find((row) => String(row.id) === String(geofenceId));
      if (!match) throw new Error('Geofence not found.');
      return match;
    }
  }, [get, geofenceId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        const vehicleList = await loadVehicles();
        if (cancelled) return;
        setVehicles(vehicleList);

        if (isEdit) {
          const existing = await loadExisting();
          if (cancelled) return;
          setForm({
            name: existing.name || 'Home',
            vehicleId: existing.vehicleId ? String(existing.vehicleId) : '',
            deviceId: existing.deviceId ? String(existing.deviceId) : '',
            latitude:
              existing.centerLat != null ? String(existing.centerLat) : '',
            longitude:
              existing.centerLng != null ? String(existing.centerLng) : '',
            radiusMeters: existing.radiusMeters || 500,
            alertOnEnter: existing.alertOnEnter,
            alertOnExit: existing.alertOnExit,
            enabled: existing.enabled,
          });
        } else {
          let matched = null;
          if (preselectVehicleId) {
            matched = vehicleList.find(
              (v) => String(v.id) === String(preselectVehicleId)
            );
          } else if (preselectDeviceId) {
            matched = vehicleList.find(
              (v) => String(v.deviceId) === String(preselectDeviceId)
            );
          }

          setForm({
            ...emptyForm,
            vehicleId: matched?.id ? String(matched.id) : '',
            deviceId: matched?.deviceId
              ? String(matched.deviceId)
              : preselectDeviceId || '',
          });
        }
      } catch (error) {
        toast.error(error?.message || 'Unable to load geofence form.');
        if (isEdit) navigate('/customer/geofences', { replace: true });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isEdit,
    loadExisting,
    loadVehicles,
    navigate,
    preselectDeviceId,
    preselectVehicleId,
  ]);

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find((v) => String(v.id) === String(vehicleId));
    updateForm({
      vehicleId: vehicleId || '',
      deviceId: vehicle?.deviceId ? String(vehicle.deviceId) : '',
    });
  };

  const handleMapChange = ({ latitude, longitude, radiusMeters }) => {
    updateForm({
      latitude: latitude ?? form.latitude,
      longitude: longitude ?? form.longitude,
      radiusMeters:
        radiusMeters === '' || radiusMeters == null
          ? form.radiusMeters
          : radiusMeters,
    });
  };

  const scrollToMap = () => {
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setMapFocusKey((k) => k + 1);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location is not available on this device.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updateForm({
          latitude: String(Number(pos.coords.latitude.toFixed(6))),
          longitude: String(Number(pos.coords.longitude.toFixed(6))),
        });
        setMapFocusKey((k) => k + 1);
        toast.success('Location updated.');
      },
      () => toast.error('Unable to get current location.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const validate = () => {
    if (!String(form.name || '').trim()) {
      toast.error('Enter a zone name.');
      return false;
    }
    if (!form.vehicleId) {
      toast.error('Select a vehicle.');
      return false;
    }
    const lat = Number.parseFloat(form.latitude);
    const lng = Number.parseFloat(form.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      toast.error('Set the zone location on the map or use GPS.');
      return false;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error('Enter valid latitude and longitude.');
      return false;
    }
    if (!Number.isFinite(safeRadius) || safeRadius < MIN_RADIUS || safeRadius > MAX_RADIUS) {
      toast.error(`Radius must be between ${MIN_RADIUS} and ${MAX_RADIUS} meters.`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    const payload = buildGeofencePayload({
      name: form.name,
      vehicleId: form.vehicleId,
      deviceId: form.deviceId || selectedVehicle?.deviceId || undefined,
      centerLat: form.latitude,
      centerLng: form.longitude,
      radiusMeters: safeRadius,
      alertOnEnter: form.alertOnEnter,
      alertOnExit: form.alertOnExit,
      enabled: form.enabled,
    });

    try {
      if (isEdit) {
        await updateCustomerGeofence(put, patch, geofenceId, payload);
        toast.success('Geofence updated.');
      } else {
        await createCustomerGeofence(post, payload);
        toast.success('Geofence created.');
      }
      navigate('/customer/geofences');
    } catch (error) {
      toast.error(error?.message || 'Failed to save geofence.');
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = isSaving || vehicles.length === 0;
  const sliderPct =
    ((safeRadius - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS)) * 100;

  const IconBox = ({ children }) => (
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
      style={{
        backgroundColor: isDarkMode ? D.iconBg : '#E4E4E7',
        color: isDarkMode ? D.primaryText : '#3F3F46',
      }}
    >
      {children}
    </span>
  );

  const FieldShell = ({ children, className = '', onClick }) => (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick();
            }
          : undefined
      }
      className={cn(
        'flex min-h-12 w-full items-center gap-3 rounded-2xl px-3 py-2.5',
        className
      )}
      style={{
        backgroundColor: C.inputBg,
        border: isDarkMode ? 'none' : `1px solid ${C.inputBorder}`,
      }}
    >
      {children}
    </div>
  );

  if (isLoading) {
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center"
        style={{ backgroundColor: C.mainBg }}
      >
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: C.secondaryText }} />
      </div>
    );
  }

  return (
    <div
      className="-mx-2 min-h-full pb-8 sm:mx-0"
      style={{ backgroundColor: C.mainBg, color: C.primaryText }}
    >
      <form
        onSubmit={handleSubmit}
        className="mx-auto w-full max-w-lg space-y-5 px-2 sm:px-0"
      >
        <div className="pt-1">
          <Link
            to="/customer/geofences"
            aria-label="Back to geofences"
            className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full no-underline"
            style={{
              backgroundColor: C.cardBg,
              color: C.primaryText,
              boxShadow: C.shadow,
            }}
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1
            className="text-[28px] font-bold leading-tight tracking-tight"
            style={{ color: C.primaryText }}
          >
            {isEdit ? 'Edit Geofence' : 'Add Geofence'}
          </h1>
          <p className="mt-1.5 text-[13px]" style={{ color: C.secondaryText }}>
            Create a safe zone for your vehicle.
          </p>
        </div>

        {/* Settings card — filled, no harsh outline */}
        <section
          className="space-y-5 rounded-[20px] p-4 sm:p-5"
          style={{
            backgroundColor: C.cardBg,
            boxShadow: C.shadow,
          }}
        >
          <div className="space-y-2">
            <label
              htmlFor="geofence-name"
              className="text-[12px] font-medium"
              style={{ color: C.secondaryText }}
            >
              Zone Name
            </label>
            <FieldShell>
              <IconBox>
                <Home className="h-4 w-4" />
              </IconBox>
              <input
                id="geofence-name"
                value={form.name}
                onChange={(e) => updateForm({ name: e.target.value })}
                placeholder="Home"
                maxLength={80}
                className="h-8 min-w-0 flex-1 border-0 bg-transparent text-[14px] outline-none"
                style={{ color: C.primaryText }}
              />
            </FieldShell>
          </div>

          <div className="space-y-2">
            <label
              className="text-[12px] font-medium"
              style={{ color: C.secondaryText }}
            >
              Vehicle
            </label>
            <Select
              value={form.vehicleId || undefined}
              onValueChange={handleVehicleChange}
            >
              <SelectTrigger
                className="h-auto min-h-12 w-full rounded-2xl border-0 px-3 py-2.5 text-[14px] shadow-none focus:ring-0"
                style={{
                  backgroundColor: C.inputBg,
                  color: C.primaryText,
                  border: isDarkMode ? 'none' : `1px solid ${C.inputBorder}`,
                }}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <IconBox>
                    <Car className="h-4 w-4" />
                  </IconBox>
                  <span
                    className="min-w-0 flex-1 truncate text-left text-[14px]"
                    style={{
                      color: form.vehicleId ? C.primaryText : C.placeholder,
                    }}
                  >
                    {selectedVehicle
                      ? selectedVehicle.vehicleName
                      : 'Select vehicle'}
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent
                style={{
                  backgroundColor: C.cardBg,
                  borderColor: isDarkMode ? D.inputBorder : C.inputBorder,
                  color: C.primaryText,
                }}
              >
                {vehicles.map((vehicle) => (
                  <SelectItem key={vehicle.id} value={String(vehicle.id)}>
                    {vehicle.vehicleName}
                    {vehicle.vehicleNumber && vehicle.vehicleNumber !== 'N/A'
                      ? ` · ${vehicle.vehicleNumber}`
                      : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {vehicles.length === 0 ? (
              <p className="text-[11px]" style={{ color: C.secondaryText }}>
                No vehicles found. Add a vehicle before creating a geofence.
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label
              className="text-[12px] font-medium"
              style={{ color: C.secondaryText }}
            >
              Current Location
            </label>
            <FieldShell>
              <IconBox>
                <MapPin className="h-4 w-4" />
              </IconBox>
              <p
                className="min-w-0 flex-1 truncate font-mono text-[12px]"
                style={{ color: hasCoords ? C.whiteText : C.placeholder }}
              >
                {coordsLabel}
              </p>
              <button
                type="button"
                onClick={scrollToMap}
                className="inline-flex shrink-0 items-center gap-0.5 text-[12px] font-medium"
                style={{ color: C.secondaryText }}
              >
                Change Location
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </FieldShell>
          </div>

          <div className="space-y-3 pt-1">
            <p
              className="text-center text-[12px] font-medium"
              style={{ color: C.secondaryText }}
            >
              Radius
            </p>
            <p
              className="text-center text-[36px] font-semibold leading-none tracking-tight"
              style={{ color: C.primaryText }}
            >
              {formatRadius(safeRadius)}
            </p>
            <div className="space-y-2 px-0.5">
              <input
                type="range"
                min={MIN_RADIUS}
                max={MAX_RADIUS}
                step={50}
                value={safeRadius}
                onChange={(e) =>
                  updateForm({ radiusMeters: Number.parseInt(e.target.value, 10) })
                }
                className="geofence-radius-slider h-1.5 w-full cursor-pointer appearance-none rounded-full"
                style={{
                  background: isDarkMode
                    ? `linear-gradient(to right, ${D.sliderActive} 0%, ${D.sliderActive} ${sliderPct}%, ${D.sliderInactive} ${sliderPct}%, ${D.sliderInactive} 100%)`
                    : `linear-gradient(to right, #18181B 0%, #18181B ${sliderPct}%, #E4E4E7 ${sliderPct}%, #E4E4E7 100%)`,
                }}
                aria-label="Geofence radius"
              />
              <div
                className="flex items-center justify-between text-[11px]"
                style={{ color: C.secondaryText }}
              >
                <span>50 m</span>
                <span>10 km</span>
              </div>
            </div>

            <button
              type="button"
              onClick={useCurrentLocation}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-[13px] font-medium transition-opacity hover:opacity-90"
              style={{
                backgroundColor: isDarkMode ? D.iconBg : C.iconBg,
                color: C.whiteText,
              }}
            >
              <Crosshair className="h-4 w-4" />
              Use Current Location
            </button>
          </div>
        </section>

        <section ref={mapSectionRef}>
          <GeofenceCirclePicker
            latitude={form.latitude}
            longitude={form.longitude}
            radiusMeters={safeRadius}
            onChange={handleMapChange}
            mapFocusKey={mapFocusKey}
          />
        </section>

        <section
          className="space-y-1 rounded-[20px] p-4 sm:p-5"
          style={{
            backgroundColor: C.cardBg,
            boxShadow: C.shadow,
          }}
        >
          <h2
            className="mb-3 text-[12px] font-medium"
            style={{ color: C.secondaryText }}
          >
            Alerts
          </h2>

          <div className="flex items-center gap-3 py-2.5">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{
                backgroundColor: isDarkMode ? D.enterIconBg : C.enterIconBg,
                color: isDarkMode ? D.enterIcon : C.enterIcon,
              }}
            >
              <LogIn className="h-4 w-4" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-[14px] font-semibold"
                style={{ color: C.primaryText }}
              >
                Enter Zone
              </p>
              <p
                className="text-[11px] leading-snug"
                style={{ color: C.secondaryText }}
              >
                Get notified when vehicle enters the zone
              </p>
            </div>
            <Switch
              checked={form.alertOnEnter}
              onCheckedChange={(checked) => updateForm({ alertOnEnter: checked })}
              className={
                isDarkMode
                  ? 'border-0 data-[state=checked]:!bg-[#F5F5F7] data-[state=unchecked]:!bg-[#54545D] [&>span]:!bg-[#FFFFFF]'
                  : 'border-0 data-[state=checked]:!bg-[#18181B] data-[state=unchecked]:!bg-[#D4D4D8] [&>span]:!bg-[#FFFFFF]'
              }
            />
          </div>

          <div className="h-px" style={{ backgroundColor: C.divider }} />

          <div className="flex items-center gap-3 py-2.5">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{
                backgroundColor: isDarkMode ? D.exitIconBg : C.exitIconBg,
                color: isDarkMode ? D.exitIcon : C.exitIcon,
              }}
            >
              <LogOut className="h-4 w-4" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-[14px] font-semibold"
                style={{ color: C.primaryText }}
              >
                Exit Zone
              </p>
              <p
                className="text-[11px] leading-snug"
                style={{ color: C.secondaryText }}
              >
                Get notified when vehicle exits the zone
              </p>
            </div>
            <Switch
              checked={form.alertOnExit}
              onCheckedChange={(checked) => updateForm({ alertOnExit: checked })}
              className={
                isDarkMode
                  ? 'border-0 data-[state=checked]:!bg-[#F5F5F7] data-[state=unchecked]:!bg-[#54545D] [&>span]:!bg-[#FFFFFF]'
                  : 'border-0 data-[state=checked]:!bg-[#18181B] data-[state=unchecked]:!bg-[#D4D4D8] [&>span]:!bg-[#FFFFFF]'
              }
            />
          </div>
        </section>

        <button
          type="submit"
          disabled={saveDisabled}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed"
          style={
            saveDisabled
              ? {
                  backgroundColor: isDarkMode ? D.buttonDisabled : C.buttonDisabled,
                  color: isDarkMode ? D.disabledText : '#FAFAFA',
                }
              : {
                  backgroundColor: isDarkMode ? D.buttonBg : C.buttonBg,
                  color: isDarkMode ? D.buttonText : C.buttonText,
                  boxShadow: isDarkMode ? D.shadow : undefined,
                }
          }
        >
          {isSaving ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <ShieldCheck className="h-5 w-5" />
              {isEdit ? 'Save Changes' : 'Save Geofence'}
            </>
          )}
        </button>
      </form>

      <style>{`
        .geofence-radius-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 9999px;
          background: ${isDarkMode ? D.sliderThumb : '#FFFFFF'};
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.35);
          cursor: pointer;
          margin-top: -8px;
        }
        .geofence-radius-slider::-webkit-slider-runnable-track {
          height: 6px;
          border-radius: 9999px;
        }
        .geofence-radius-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 9999px;
          background: ${isDarkMode ? D.sliderThumb : '#FFFFFF'};
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.35);
          cursor: pointer;
        }
        #geofence-name::placeholder {
          color: ${C.placeholder};
          opacity: 1;
        }
      `}</style>
    </div>
  );
}
